import logger from '../diagnostics/logging/logger';
import {
    ApolloClient,
    HttpLink,
    ApolloLink,
    Observable,
    split,
    from,
} from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import { apiBaseUrl } from '../config/index';
import generateId from '../tools/idGenerator';
import * as types from '../actions/actionTypes';
import store from 'store';
import ReadErrorUnknownCauseMessage from '../components/ui/messages/wordage/errors/ReadErrorUnknownCauseMessage';
import WriteRejectedErrorSafeToRetryMessage from '../components/ui/messages/wordage/errors/WriteRejectedErrorSafeToRetryMessage';
import WriteUnknownResultErrorSafeToRetryMessage from '../components/ui/messages/wordage/errors/WriteUnknownResultErrorSafeToRetryMessage';
import WriteUnknownResultErrorCheckBeforeRetryMessage from '../components/ui/messages/wordage/errors/WriteUnknownResultErrorCheckBeforeRetryMessage';
import { sessionId } from './session';
import { authenticationService } from '_legacy/auth/AuthenticationService';
import { createClient } from 'graphql-ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { versionReceived } from '_legacy/actions/versionActions';

let _graphQlClient = undefined;

//code below is largely taken from https://www.apollographql.com/docs/react/advanced/boost-migration.html
const addSessionContextToRequest = async (operation) => {
    const token = await authenticationService.getAccessTokenAsync();
    if (token) {
        const { headers: existingHeaders = {} } = operation.getContext();
        operation.setContext({
            headers: {
                ...existingHeaders,
                authorization: encodeURIComponent(token),
                fortSessionId: sessionId,
            },
        });
    }
};

const sessionContextAddingMiddleware = new ApolloLink(
    (operation, forward) =>
        new Observable((observer) => {
            let handle;
            Promise.resolve(operation)
                .then((oper) => addSessionContextToRequest(oper))
                .then(() => {
                    handle = forward(operation).subscribe({
                        next: observer.next.bind(observer),
                        error: observer.error.bind(observer),
                        complete: observer.complete.bind(observer),
                    });
                })
                .catch((err) => {
                    logger.warn(
                        `[sessionContextMiddleware] Failed to get token for "${operation.operationName}": ${err?.message}`
                    );
                    observer.error(err);
                });

            return () => {
                if (handle) handle.unsubscribe();
            };
        })
);

const createHttpWithWebSocketUpdatesLink = ({
    httpResource,
    webSocketClient,
}) => {
    // using the ability to split links, you can send data to each link
    // depending on what kind of operation is being sent
    return split(
        // split based on operation type
        ({ query }) => {
            const { kind, operation } = getMainDefinition(query);
            return (
                kind === 'OperationDefinition' && operation === 'subscription'
            );
        },
        new GraphQLWsLink(webSocketClient),
        new HttpLink(httpResource)
    );
};

const graphQlClient = () => {
    if (!_graphQlClient) {
        const subscriptionClient = createSocketSubscription();
        const mainLink = ApolloLink.from([
            onError(({ graphQLErrors, networkError, operation, forward }) => {
                const statusCode = networkError && networkError.statusCode;
                const isUnauthorised = statusCode === 401 || statusCode === 403;

                // ── Diagnostic: log every error that reaches onError ──
                const opDefs =
                    operation?.query?.definitions?.filter(
                        (d) => d.kind === 'OperationDefinition'
                    ) || [];
                const opType =
                    opDefs.map((d) => d.operation).join(',') || 'unknown';
                logger.warn(
                    `[onError] operation="${operation.operationName}" type=${opType}` +
                        ` | networkError=${
                            networkError
                                ? `${networkError.name}: ${networkError.message} (status=${statusCode})`
                                : 'none'
                        }` +
                        ` | graphQLErrors=${
                            graphQLErrors
                                ? graphQLErrors.map((e) => e.message).join('; ')
                                : 'none'
                        }`
                );

                if (isUnauthorised) {
                    const { alreadyRetried } = operation.getContext();
                    if (alreadyRetried) {
                        logger.warn(
                            'Token refresh retry already attempted, giving up'
                        );
                    } else {
                        return new Observable((observer) => {
                            authenticationService
                                .getAccessTokenAsync()
                                .then((token) => {
                                    if (!token) {
                                        throw new Error(
                                            'Token refresh returned no token during 401 retry'
                                        );
                                    }
                                    const { headers: existingHeaders = {} } =
                                        operation.getContext();
                                    operation.setContext({
                                        alreadyRetried: true,
                                        headers: {
                                            ...existingHeaders,
                                            authorization:
                                                encodeURIComponent(token),
                                            fortSessionId: sessionId,
                                        },
                                    });
                                })
                                .then(() => {
                                    forward(operation).subscribe({
                                        next: observer.next.bind(observer),
                                        error: observer.error.bind(observer),
                                        complete:
                                            observer.complete.bind(observer),
                                    });
                                })
                                .catch((refreshError) => {
                                    logger.warn(
                                        'Token refresh failed during 401 retry: ' +
                                            refreshError.message
                                    );
                                    observer.error(networkError);
                                });
                        });
                    }
                }

                // Retry once on transient network errors (e.g. browser waking
                // from sleep/inactivity). "Failed to fetch" with no statusCode
                // means the request never reached the server, so it is always
                // safe to retry.
                const isTransientNetworkError =
                    networkError instanceof TypeError && !statusCode;

                if (isTransientNetworkError) {
                    const { networkRetryCount = 0 } = operation.getContext();
                    if (networkRetryCount < 1) {
                        logger.warn(
                            `[onError] Transient network error for "${
                                operation.operationName
                            }" — retrying (attempt ${networkRetryCount + 1})`
                        );
                        operation.setContext({
                            networkRetryCount: networkRetryCount + 1,
                        });
                        return forward(operation);
                    }
                }

                if (graphQLErrors) {
                    graphQLErrors.forEach((e) =>
                        logger.error(
                            /* error: */ e,
                            /* props: */ {
                                operationName: operation.operationName,
                            }
                        )
                    );
                }

                if (networkError) {
                    logger.error(
                        /* error: */ networkError,
                        /* props: */ {
                            operationName: operation.operationName,
                            // See https://www.apollographql.com/docs/link/links/http.html#error
                            url: networkError?.response?.url,
                            statusCode: networkError.statusCode,
                            bodyText: networkError.bodyText,
                        }
                    );
                }

                // Determine if this is a subscription operation
                const operationDefinitions =
                    operation?.query?.definitions?.filter(
                        (d) => d.kind === 'OperationDefinition'
                    ) || [];
                const isSubscription = operationDefinitions.some(
                    (d) => d.operation === 'subscription'
                );

                logger.warn(
                    `[onError] ▶ DISPATCHING RED BANNER for "${operation.operationName}" (type=${opType}, isSubscription=${isSubscription})`
                );
                store.dispatch({
                    type: types.NOTIFICATION_MESSAGE_ADDED,
                    message: _getUserNotificationMessageForError({
                        graphQLErrors,
                        networkError,
                        operation,
                    }),
                });
            }),
            sessionContextAddingMiddleware,
            createHttpWithWebSocketUpdatesLink({
                httpResource: {
                    uri: `https://${apiBaseUrl}/graphql`,
                    credentials: 'same-origin',
                },
                webSocketClient: subscriptionClient,
            }),
        ]);

        function _getUserNotificationMessageForError({
            graphQLErrors,
            networkError,
            operation,
        }) {
            const operationTypes =
                operation &&
                operation.query &&
                operation.query.definitions.length &&
                operation.query.definitions
                    .filter(
                        (queryDef) => queryDef.kind === 'OperationDefinition'
                    )
                    .map((queryDef) => queryDef.operation);
            const isRead =
                operationTypes &&
                operationTypes.every(
                    (oT) => oT === 'query' || oT === 'subscription'
                );

            const context = operation.getContext();
            const errorHandlingArgs = context && context.errorHandling;

            if (isRead)
                return {
                    typeId: 'READ_ERROR_FOR_UNKNOWN_REASON',
                    getMessageRichContent: (props) => (
                        <ReadErrorUnknownCauseMessage
                            {...props}
                            resultDescription={
                                errorHandlingArgs &&
                                errorHandlingArgs.rejectedResult &&
                                errorHandlingArgs.rejectedResult
                                    .resultDescription &&
                                errorHandlingArgs.rejectedResult.resultDescription()
                            }
                            resolutionToTryBeforeContacting={
                                errorHandlingArgs &&
                                errorHandlingArgs.rejectedResult &&
                                errorHandlingArgs.rejectedResult.resolution &&
                                errorHandlingArgs.rejectedResult.resolution
                                    .tryBeforeContacting &&
                                errorHandlingArgs.rejectedResult.resolution.tryBeforeContacting()
                            }
                        />
                    ),
                };
            else {
                const responseStatus =
                    networkError &&
                    networkError.response &&
                    networkError.response.statusCode;

                const isHttpInvalidRequest =
                    responseStatus >= 400 && responseStatus <= 499;
                const isGraphQlInvalidRequest =
                    graphQLErrors &&
                    graphQLErrors.length &&
                    graphQLErrors.every(
                        (error) =>
                            error &&
                            error.extensions &&
                            error.extensions.code === 'INVALID_REQUEST'
                    );

                if (isHttpInvalidRequest || isGraphQlInvalidRequest)
                    return {
                        typeId: 'WRITE_REJECTED_FOR_UNKNOWN_REASON_RETRY_SAFE',
                        getMessageRichContent: (props) => (
                            <WriteRejectedErrorSafeToRetryMessage
                                {...props}
                                resultDescription={
                                    errorHandlingArgs.rejectedResult &&
                                    errorHandlingArgs.rejectedResult
                                        .resultDescription &&
                                    errorHandlingArgs.rejectedResult.resultDescription()
                                }
                                resolutionToTryBeforeContacting={
                                    errorHandlingArgs &&
                                    errorHandlingArgs.rejectedResult &&
                                    errorHandlingArgs.rejectedResult
                                        .resolution &&
                                    errorHandlingArgs.rejectedResult.resolution
                                        .tryBeforeContacting &&
                                    errorHandlingArgs.rejectedResult.resolution.tryBeforeContacting()
                                }
                            />
                        ),
                    };
                else {
                    const unknownResultErrorHandlingArgs =
                        errorHandlingArgs && errorHandlingArgs.unknownResult;
                    const isRetrySafe = !!(
                        unknownResultErrorHandlingArgs &&
                        unknownResultErrorHandlingArgs.resolution &&
                        unknownResultErrorHandlingArgs.resolution.isSafeToRetry
                    );
                    if (isRetrySafe)
                        return {
                            typeId: 'WRITE_UNKNOWN_RESULT_RETRY_SAFE',
                            getMessageRichContent: (props) => (
                                <WriteUnknownResultErrorSafeToRetryMessage
                                    {...props}
                                    resultDescription={
                                        unknownResultErrorHandlingArgs &&
                                        unknownResultErrorHandlingArgs.resultDescription &&
                                        unknownResultErrorHandlingArgs.resultDescription()
                                    }
                                    resolutionToTryBeforeContacting={
                                        unknownResultErrorHandlingArgs &&
                                        unknownResultErrorHandlingArgs.resolution &&
                                        unknownResultErrorHandlingArgs
                                            .resolution.tryBeforeContacting &&
                                        unknownResultErrorHandlingArgs.resolution.tryBeforeContacting()
                                    }
                                />
                            ),
                        };
                    else
                        return {
                            typeId: 'WRITE_UNKNOWN_RESULT_RETRY_UNSAFE',
                            getMessageRichContent: (props) => (
                                <WriteUnknownResultErrorCheckBeforeRetryMessage
                                    {...props}
                                    resultDescription={
                                        unknownResultErrorHandlingArgs &&
                                        unknownResultErrorHandlingArgs.resultDescription &&
                                        unknownResultErrorHandlingArgs.resultDescription()
                                    }
                                    resolutionToTryBeforeContacting={
                                        unknownResultErrorHandlingArgs &&
                                        unknownResultErrorHandlingArgs.resolution &&
                                        unknownResultErrorHandlingArgs
                                            .resolution.tryBeforeContacting &&
                                        unknownResultErrorHandlingArgs.resolution.tryBeforeContacting()
                                    }
                                />
                            ),
                        };
                }
            }
        }

        const dependencyStartTracker = setContext(() => ({
            requestStartTime: new Date().getTime(),
        }));

        const dependencySuccessTracker = new ApolloLink(
            (operation, forward) => {
                return forward(operation).map((response) => {
                    const currentTime = new Date().getTime();
                    const startTime = operation.getContext().requestStartTime;
                    const requestElapsedTime = currentTime - startTime;

                    // Apollo deals with the operation at a high level, so I've just put some place holders
                    // in for type and url and host.
                    logger.trackDependency(
                        generateId(),
                        'POST',
                        'graph',
                        '/graph',
                        requestElapsedTime,
                        true,
                        200
                    );

                    return response;
                });
            }
        );

        const dependencyErrorTracker = onError(({ operation }) => {
            const currentTime = new Date().getTime();
            const startTime = operation.getContext().requestStartTime;
            const requestElapsedTime = currentTime - startTime;
            // Apollo deals with the operation at a high level, so I've just put some place holders
            // in for type and url and host.
            logger.trackDependency(
                generateId(),
                'POST',
                'graph',
                '/graph',
                requestElapsedTime,
                false,
                0
            );
        });

        _graphQlClient = new ApolloClient({
            link: from([
                dependencySuccessTracker,
                dependencyErrorTracker,
                dependencyStartTracker,
                mainLink,
            ]),
            cache: new InMemoryCache(),
        });
    }
    return _graphQlClient;
};

const createSocketSubscription = () => {
    const handleConnected = (e) => {
        store.dispatch({ type: types.SOCKET_CONNECTED });
    };

    const handleDisconnected = (e) => {
        if (e.wasClean) {
            return;
        }
        store.dispatch({ type: types.SOCKET_DISCONNECTED });
    };

    const handleError = (e) => {
        logger.warn(e.message);
    };

    const handlePong = (received, payload) => {
        store.dispatch(versionReceived(payload.version));
    };

    const client = createClient({
        url: `wss://${apiBaseUrl}/graphql`,
        lazy: true,
        connectionParams: async () => {
            const token = await authenticationService.getAccessTokenAsync();
            if (!token) return { fortSessionId: sessionId };
            return {
                authorization: encodeURIComponent(token),
                fortSessionId: sessionId,
            };
        },
        on: {
            closed: handleDisconnected,
            connected: handleConnected,
            error: handleError,
            pong: handlePong,
        },
    });
    return client;
};

export default graphQlClient;
